import React, { useState, useEffect } from 'react';
import { getApiKey,getApiBaseUrl } from '../utils/config';
import './JobPostingsModal.css';
import poweredByLogo from '../logoimages/Poweredby_CareerOneStop_logo.jpg';
import { exportJobPostingsToExcel } from '../utils/excelExport';
import { useTooltip } from '../contexts/TooltipContext';

interface JobPosting {
  JvId: string;
  JobTitle: string;
  Company: string;
  DescriptionSnippet: string;
  Distance: string;
  AcquisitionDate: string;
  URL: string;
  Location: string;
  Fc: boolean;
  OnetCodes: string[];
}

interface JobPostingsModalProps {
  isVisible: boolean;
  onClose: () => void;
  socCode: string;
  jobTitle: string;
}

const JobPostingsModal: React.FC<JobPostingsModalProps> = ({ 
  isVisible, 
  onClose, 
  socCode, 
  jobTitle 
}) => {
  // Tooltip context for global tooltip state
  const { setIsTooltipOpen } = useTooltip();
  
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);
  const [hasMoreJobs, setHasMoreJobs] = useState(false);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [viewingSavedJobs, setViewingSavedJobs] = useState(false);
  const [savedJobsList, setSavedJobsList] = useState<JobPosting[]>([]);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'info'} | null>(null);
  const [radius, setRadius] = useState(22); // Default 22 miles for Sumner County
  const [sortBy, setSortBy] = useState<'date' | 'distance'>('date'); // Frontend sorting only
  const [showRadiusTooltip, setShowRadiusTooltip] = useState(false);
  const [isFiltersMinimized, setIsFiltersMinimized] = useState(false);
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const filtersRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && socCode) {
      fetchJobPostings();
    }
  }, [isVisible, socCode]);
  
  // Reset to page 0 when radius changes
  useEffect(() => {
    setCurrentPage(0);
  }, [radius]);

  // Close tooltip when clicking outside (with delay to prevent immediate close)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking the info button or the tooltip itself
      if (target.closest('[aria-label="Information about search radius"]') || 
          target.closest('.radius-tooltip')) {
        return;
      }
      setShowRadiusTooltip(false);
      setIsTooltipOpen(false);
    };
    if (showRadiusTooltip) {
      // Add small delay to prevent immediate trigger
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showRadiusTooltip]);

  // Load saved jobs from localStorage on component mount
  useEffect(() => {
    const savedJobsFromStorage = JSON.parse(localStorage.getItem('savedJobs') || '[]');
    const savedJobIds = new Set<string>(savedJobsFromStorage.map((job: any) => job.JvId));
    setSavedJobs(savedJobIds);
    setSavedJobsList(savedJobsFromStorage);
  }, []);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Scroll detection for minimizing filters on mobile
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth <= 768 && contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        const shouldMinimize = scrollTop > 50; // Minimize after 50px scroll
        
        if (shouldMinimize !== isFiltersMinimized) {
          setIsFiltersMinimized(shouldMinimize);
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [isFiltersMinimized]);

  // Reset filters minimization when modal opens/closes
  useEffect(() => {
    if (!isVisible) {
      setIsFiltersMinimized(false);
    }
  }, [isVisible]);

  const fetchJobPostings = async (page: number = 0, append: boolean = false) => {
    setLoading(true);
    setError(null);
    
    // Clear jobs if not appending (e.g., new search)
    if (!append) {
      setJobPostings([]);
      setTotalJobs(0);
    }
    
    try {
      // Use the correct CareerOneStop v2/jobsearch endpoint with pagination
      const apiKey = await getApiKey();
      const apiBaseUrl = await getApiBaseUrl();

      const location = 'Gallatin,TN'; // Sumner County center
      // First load gets 40 jobs, subsequent loads get 20 jobs
      const pageSize = page === 0 ? 40 : 20;
      // Calculate startRecord: 0 for first, then 40, 60, 80, etc.
      const startRecord = page === 0 ? 0 : 40 + ((page - 1) * 20);
      
      const response = await fetch(`${apiBaseUrl}/api/career-one-stop-proxy/jobs?soc=${encodeURIComponent(socCode)}&location=${encodeURIComponent(location)}&radius=${radius}&page=${page}&pageSize=${pageSize}&startRecord=${startRecord}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        }
      });

      if (!response.ok) {
        // Handle specific error cases with student-friendly messages
        if (response.status === 404 || response.status === 502) {
          setError(`No jobs found within ${radius} miles of Gallatin, TN. Try:\n• Expanding your search radius to 30 or 50 miles\n• Checking the "Industries" tab to find companies hiring for this role`);
          setJobPostings([]);
          setTotalJobs(0);
          setLoading(false);
          return;
        } else if (response.status === 403) {
          setError('Too many searches. Please wait a moment and try again.');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newJobs = data.Jobs || [];
      
      // Extract total count from API response (try different field names)
      const apiTotalCount = data.TotalCount || data.totalCount || data.TotalJobs || data.total || null;
      
      if (append) {
        setJobPostings(prev => {
          // Get existing job IDs to avoid duplicates
          const existingIds = new Set(prev.map((job: JobPosting) => job.JvId));
          // Filter out jobs that already exist
          const uniqueNewJobs = newJobs.filter((job: JobPosting) => !existingIds.has(job.JvId));
          return [...prev, ...uniqueNewJobs];
        });
      } else {
        setJobPostings(newJobs);
      }
      
      // Update pagination info
      if (apiTotalCount !== null && apiTotalCount > 0) {
        // We got a total from API - use it!
        setTotalJobs(apiTotalCount);
      } else {
        // No total from API - estimate based on loaded jobs
        if (append) {
          // When loading more, update total to current loaded count
          const currentTotal = jobPostings.length + newJobs.length;
          setTotalJobs(currentTotal);
        } else {
          // First load - set to newJobs length for now
          setTotalJobs(newJobs.length);
        }
      }
      // Show "Load More" if we got a full page - means there might be more
      // First page gets 40 jobs, subsequent pages get 20 jobs
      const expectedPageSize = page === 0 ? 40 : 20;
      setHasMoreJobs(newJobs.length === expectedPageSize);
      setCurrentPage(page);
      
      // Scroll to new jobs after loading more (only when appending)
      if (append && newJobs.length > 0) {
        setTimeout(() => {
          // Use requestAnimationFrame to prevent forced reflow
          requestAnimationFrame(() => {
            loadMoreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }, 100);
      }
    } catch (err) {
      console.error('Error fetching job postings:', err);
      setError('Could not connect to job search service. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setJobPostings([]);
    setError(null);
    setExpandedDescriptions(new Set());
    setCurrentPage(0);
    setTotalJobs(0);
    setHasMoreJobs(false);
    onClose();
  };

  // Get sorted jobs based on current sort option (frontend sorting only)
  const getSortedJobs = () => {
    const jobsToSort = [...jobPostings];
    
    if (sortBy === 'distance') {
      // Sort by distance (closest first)
      return jobsToSort.sort((a, b) => {
        const distA = parseFloat(a.Distance) || 999;
        const distB = parseFloat(b.Distance) || 999;
        return distA - distB;
      });
    } else {
      // Sort by date (newest first)
      return jobsToSort.sort((a, b) => {
        const dateA = new Date(a.AcquisitionDate).getTime();
        const dateB = new Date(b.AcquisitionDate).getTime();
        return dateB - dateA; // Newest first
      });
    }
  };

  const toggleDescription = (index: number) => {
    const newExpanded = new Set(expandedDescriptions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedDescriptions(newExpanded);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleSaveJob = (job: JobPosting) => {
    const jobId = job.JvId;
    const newSavedJobs = new Set(savedJobs);
    
    if (newSavedJobs.has(jobId)) {
      newSavedJobs.delete(jobId);
      // Remove from localStorage
      const savedJobsFromStorage = JSON.parse(localStorage.getItem('savedJobs') || '[]');
      const updatedSavedJobs = savedJobsFromStorage.filter((savedJob: any) => savedJob.JvId !== jobId);
      localStorage.setItem('savedJobs', JSON.stringify(updatedSavedJobs));
      setSavedJobsList(updatedSavedJobs);
      setNotification({message: 'Job removed from saved jobs', type: 'info'});
    } else {
      newSavedJobs.add(jobId);
      // Add to localStorage
      const savedJobsFromStorage = JSON.parse(localStorage.getItem('savedJobs') || '[]');
      const newJobWithTimestamp = {
        ...job,
        savedAt: new Date().toISOString()
      };
      savedJobsFromStorage.push(newJobWithTimestamp);
      localStorage.setItem('savedJobs', JSON.stringify(savedJobsFromStorage));
      setSavedJobsList(savedJobsFromStorage);
      setNotification({message: 'Job saved successfully!', type: 'success'});
    }
    
    setSavedJobs(newSavedJobs);
  };

  const handleShareJob = async (job: JobPosting) => {
    const shareData = {
      title: job.JobTitle,
      text: `${job.JobTitle} at ${job.Company} - ${job.Location}`,
      url: job.URL
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard
        const shareText = `${job.JobTitle} at ${job.Company}\n${job.Location}\n${job.URL}`;
        await navigator.clipboard.writeText(shareText);
        
        // Show a temporary notification
        const notification = document.createElement('div');
        notification.textContent = 'Job link copied to clipboard!';
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #d5a32a;
          color: white;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          z-index: 10002;
          font-weight: 600;
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 3000);
      }
    } catch (error) {
      console.error('Error sharing job:', error);
      // Fallback: Open in new window
      window.open(job.URL, '_blank');
    }
  };

  const isJobSaved = (jobId: string) => {
    return savedJobs.has(jobId);
  };

  const handleViewSavedJobs = () => {
    setViewingSavedJobs(true);
    setError(null);
    setLoading(false);
  };

  const handleViewAllJobs = () => {
    setViewingSavedJobs(false);
    setError(null);
    setLoading(false);
  };

  const handleRemoveFromSaved = (jobId: string) => {
    const newSavedJobs = new Set(savedJobs);
    newSavedJobs.delete(jobId);
    setSavedJobs(newSavedJobs);
    
    // Update localStorage
    const savedJobsFromStorage = JSON.parse(localStorage.getItem('savedJobs') || '[]');
    const updatedSavedJobs = savedJobsFromStorage.filter((savedJob: any) => savedJob.JvId !== jobId);
    localStorage.setItem('savedJobs', JSON.stringify(updatedSavedJobs));
    setSavedJobsList(updatedSavedJobs);
  };

  if (!isVisible) return null;

  return (
    <div className="job-postings-modal-overlay">
      <div className="job-postings-modal">
        <div className="job-postings-modal-header">
          <div className="job-postings-modal-title">
            <div className="job-postings-title-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"/>
              </svg>
            </div>
            <div className="job-postings-title-content">
              <h2>Job Opportunities</h2>
            <p className="job-postings-modal-subtitle">
                {jobTitle} • SOC: {socCode}
            </p>
            </div>
          </div>
          <div className="job-postings-header-actions">
            <div className="job-postings-powered-by">
              <img 
                src={poweredByLogo} 
                alt="Powered by CareerOneStop" 
                className="job-postings-logo"
              />
            </div>
            {savedJobsList.length > 0 && (
              <button 
                className={`job-postings-view-saved-button ${viewingSavedJobs ? 'active' : ''}`}
                onClick={viewingSavedJobs ? handleViewAllJobs : handleViewSavedJobs}
                title={viewingSavedJobs ? "View All Jobs" : "View Saved Jobs"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                </svg>
                {viewingSavedJobs ? 'All Jobs' : `Saved (${savedJobsList.length})`}
              </button>
            )}
            {!viewingSavedJobs && jobPostings.length > 0 && (
              <button 
                className="job-postings-download-button"
                onClick={() => exportJobPostingsToExcel(jobPostings, jobTitle, socCode)}
                title="Download Excel"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Download Excel
              </button>
            )}
          <button 
            className="job-postings-modal-close"
            onClick={handleClose}
              title="Close Modal"
          >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
              Close
          </button>
          </div>
        </div>

        <div className={`job-postings-modal-filters ${isFiltersMinimized ? 'minimized' : ''}`} ref={filtersRef}>
          <div className="job-postings-filter-group">
            <label htmlFor="radius-filter" style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
              Search Radius:
              <button
                type="button"
                className="inline-flex items-center justify-center cursor-pointer border-0 bg-transparent p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowRadiusTooltip(!showRadiusTooltip);
                  setIsTooltipOpen(!showRadiusTooltip);
                }}
                onMouseEnter={() => {
                  if (!showRadiusTooltip) {
                    setShowRadiusTooltip(true);
                    setIsTooltipOpen(true);
                  }
                }}
                style={{ marginLeft: '4px' }}
                aria-label="Information about search radius"
                tabIndex={0}
              >
                <svg style={{ width: '14px', height: '14px', color: '#d5a32a' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </button>
              {showRadiusTooltip && (
                <div 
                  className="radius-tooltip absolute left-0 top-full mt-2 z-[99999] w-64 p-3 bg-white border-2 border-[#d5a32a] rounded-lg shadow-lg text-sm text-gray-700"
                  style={{ whiteSpace: 'normal' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  We start your search from Gallatin, TN. A 22-mile radius covers most of Sumner County, but you can expand it (up to 50 miles) to include the wider Nashville area.
                  <div className="absolute -top-2 left-4 w-3 h-3 bg-white border-t-2 border-l-2 border-[#d5a32a] transform rotate-45"></div>
                </div>
              )}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                id="radius-filter"
                value={radius}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val >= 1 && val <= 50) {
                    setRadius(val);
                  }
                }}
                min="1"
                max="50"
                className="job-postings-filter-select"
                style={{ width: '80px', textAlign: 'center' }}
                placeholder="22"
              />
              <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>miles (max 50)</span>
            </div>
          </div>
          <div className="job-postings-filter-group">
            <label htmlFor="days-filter">Posted within:</label>
            <select
              id="days-filter"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="job-postings-filter-select"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
            </select>
          </div>
          <div className="job-postings-filter-group">
            <label htmlFor="sort-filter">Sort by:</label>
            <select
              id="sort-filter"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'distance')}
              className="job-postings-filter-select"
            >
              <option value="date">📅 Date (Newest First)</option>
              <option value="distance">📍 Distance (Closest First)</option>
            </select>
          </div>
          <button 
            onClick={() => fetchJobPostings(0, false)}
            disabled={loading}
            className="job-postings-search-button"
          >
            {loading ? 'Searching...' : 'Search Jobs'}
          </button>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`job-postings-notification ${notification.type}`}>
            <div className="job-postings-notification-content">
              <svg className="job-postings-notification-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {notification.type === 'success' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
              <span className="job-postings-notification-message">{notification.message}</span>
              <button 
                className="job-postings-notification-close"
                onClick={() => setNotification(null)}
                title="Close notification"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="job-postings-modal-content" ref={contentRef}>
          {loading && (
            <div className="job-postings-loading">
              <div className="job-postings-spinner"></div>
              <p>Searching for job postings...</p>
            </div>
          )}

          {error && (
            <div className="job-postings-error">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>{error}</p>
              <button onClick={() => fetchJobPostings(0, false)} className="job-postings-retry-button">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && !viewingSavedJobs && jobPostings.length === 0 && (
            <div className="job-postings-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3>No Job Postings Found in This Area</h3>
              <p>No active job postings found within {radius} miles of Gallatin, TN.</p>
              <div style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
                <p><strong>What you can do:</strong></p>
                <ul style={{ textAlign: 'left', marginTop: '8px', listStyle: 'disc', paddingLeft: '20px' }}>
                  <li>Try expanding your search radius to 30 or 50 miles</li>
                  <li>Check the "Industries" tab to see companies that hire for this role</li>
                  <li>Visit the "Businesses" tab for local employers</li>
                </ul>
              </div>
            </div>
          )}

          {!loading && !error && viewingSavedJobs && savedJobsList.length === 0 && (
            <div className="job-postings-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
              </svg>
              <h3>No Saved Jobs</h3>
              <p>You haven't saved any jobs yet. Click the bookmark icon on any job to save it.</p>
            </div>
          )}

          {!loading && !error && !viewingSavedJobs && jobPostings.length > 0 && (
            <div className="job-postings-results">
              <div className="job-postings-results-header">
                <h3>{totalJobs} job{totalJobs !== 1 ? 's' : ''} available</h3>
              </div>
              <div className="job-postings-list">
                {getSortedJobs().map((job, index) => (
                  <React.Fragment key={index}>
                    {/* Add scroll marker before newly loaded jobs */}
                    {/* First page = 40 jobs (index 40), subsequent pages add 20 each */}
                    {index === (currentPage === 1 ? 40 : 40 + ((currentPage - 1) * 20)) && currentPage > 0 && (
                      <div ref={loadMoreRef} style={{ height: '1px', visibility: 'hidden' }} />
                    )}
                    <div className="job-posting-card">
                    <div className="job-posting-card-header">
                      <div className="job-posting-main-info">
                        <div className="job-posting-title-section">
                      <h4 className="job-posting-title">{job.JobTitle}</h4>
                          <div className="job-posting-company">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                            {job.Company}
                          </div>
                        </div>
                        <div className="job-posting-badges">
                          <div className="job-posting-distance-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            {job.Distance} mi
                          </div>
                          {job.Fc && (
                            <div 
                              className="job-posting-featured-badge" 
                              title="This employer is a federal contractor - a company that does business with the U.S. government"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"/>
                              </svg>
                              Federal Contractor
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="job-posting-meta-info">
                      <div className="job-posting-meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <span>{job.Location}</span>
                      </div>
                      <div className="job-posting-meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        <span>Posted {formatDate(job.AcquisitionDate)}</span>
                      </div>
                    </div>

                    <div className="job-posting-description">
                      <div className="job-posting-description-header">
                        <h5>Job Description</h5>
                      </div>
                      <div className="job-posting-description-content">
                        <p>{job.DescriptionSnippet}</p>
                      </div>
                    </div>
                    
                    <div className="job-posting-actions">
                      <a 
                        href={job.URL} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="job-posting-apply-button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                        Apply Now
                      </a>
                      <div className="job-posting-secondary-actions">
                        <button 
                          className={`job-posting-save-button ${isJobSaved(job.JvId) ? 'saved' : ''}`}
                          onClick={() => handleSaveJob(job)}
                          title={isJobSaved(job.JvId) ? 'Remove from Saved' : 'Save Job'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                          </svg>
                        </button>
                        <button 
                          className="job-posting-share-button" 
                          onClick={() => handleShareJob(job)}
                          title="Share Job"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  </React.Fragment>
                ))}
              </div>
              {hasMoreJobs && (
                <div className="job-postings-load-more-container">
                  <button 
                    onClick={() => fetchJobPostings(currentPage + 1, true)}
                    disabled={loading}
                    className="job-postings-load-more"
                  >
                    {loading ? 'Loading...' : 'Load More Jobs'}
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && !error && viewingSavedJobs && savedJobsList.length > 0 && (
            <div className="job-postings-results">
              <div className="job-postings-results-header">
                <h3>Your Saved Jobs ({savedJobsList.length})</h3>
                <p className="job-postings-saved-subtitle">Jobs you've saved for later review</p>
              </div>
              <div className="job-postings-list">
                {savedJobsList.map((job, index) => (
                  <div key={index} className="job-posting-card saved-job-card">
                    <div className="job-posting-card-header">
                      <div className="job-posting-main-info">
                        <div className="job-posting-title-section">
                          <h4 className="job-posting-title">{job.JobTitle}</h4>
                          <div className="job-posting-company">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                            {job.Company}
                          </div>
                        </div>
                        <div className="job-posting-badges">
                          <div className="job-posting-distance-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            {job.Distance} mi
                          </div>
                          <div className="job-posting-saved-badge">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                            </svg>
                            Saved
                          </div>
                          {job.Fc && (
                            <div 
                              className="job-posting-featured-badge" 
                              title="This employer is a federal contractor - a company that does business with the U.S. government"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"/>
                              </svg>
                              Federal Contractor
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="job-posting-meta-info">
                      <div className="job-posting-meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <span>{job.Location}</span>
                      </div>
                      <div className="job-posting-meta-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        <span>Posted {formatDate(job.AcquisitionDate)}</span>
                      </div>
                    </div>

                    <div className="job-posting-description">
                      <div className="job-posting-description-header">
                        <h5>Job Description</h5>
                      </div>
                      <div className="job-posting-description-content">
                        <p>{job.DescriptionSnippet}</p>
                      </div>
                    </div>
                    
                    <div className="job-posting-actions">
                      <a 
                        href={job.URL} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="job-posting-apply-button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                        Apply Now
                      </a>
                      <div className="job-posting-secondary-actions">
                        <button 
                          className="job-posting-save-button saved"
                          onClick={() => handleRemoveFromSaved(job.JvId)}
                          title="Remove from Saved"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                        <button 
                          className="job-posting-share-button" 
                          onClick={() => handleShareJob(job)}
                          title="Share Job"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPostingsModal;
